/**
 * Адреса портала — history API, без библиотеки маршрутизации.
 *
 * Экранов немного, вложенность одна (`/receipts/:id`), а ссылка из бота
 * обязана открыть ровно тот объект, на который ведёт. `pushState` — чтобы
 * «назад» браузера возвращал к очереди, а не выбрасывал с портала.
 */

import { useEffect, useState } from 'react';
import type { AnchorHTMLAttributes, MouseEvent } from 'react';

export interface Route {
  path: string;
  params: URLSearchParams;
}

const EVENT = 'portal:navigate';

function read(): Route {
  return {
    path: window.location.pathname.replace(/\/+$/, '') || '/',
    params: new URLSearchParams(window.location.search),
  };
}

export function navigate(to: string, { replace = false } = {}): void {
  const current = `${window.location.pathname}${window.location.search}`;
  if (to === current) return;
  if (replace) window.history.replaceState(null, '', to);
  else window.history.pushState(null, '', to);
  window.dispatchEvent(new Event(EVENT));
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(read);
  useEffect(() => {
    const update = () => setRoute(read());
    window.addEventListener('popstate', update);
    window.addEventListener(EVENT, update);
    return () => {
      window.removeEventListener('popstate', update);
      window.removeEventListener(EVENT, update);
    };
  }, []);
  return route;
}

/** Ссылка портала: обычный `<a>` (открыть в новой вкладке работает), но без перезагрузки. */
export function Link({
  to,
  onClick,
  ...rest
}: { to: string } & AnchorHTMLAttributes<HTMLAnchorElement>) {
  const external = /^https?:\/\//.test(to);
  const handle = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (event.defaultPrevented || external) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    navigate(to);
  };
  return (
    <a
      href={to}
      onClick={handle}
      {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
      {...rest}
    />
  );
}
